import {
  createContext,
  useRef,
  forwardRef,
  useLayoutEffect,
  type HTMLAttributes,
  type DOMAttributes,
  type RefObject,
  type ForwardedRef,
} from 'react';
import * as React from 'react';
import clsx from 'clsx';
import { Overlay, useModalOverlay } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';
import styles from './ModalOverlay.module.css';

type Props = {
  onDismiss?: () => void;
  isOpen: boolean;
  'data-testid'?: string;
  initialFocusRef?: RefObject<HTMLElement>;
} & HTMLAttributes<HTMLDivElement>;

export interface ModalContext {
  modalProps: DOMAttributes<Element>;
  ref: React.MutableRefObject<HTMLDivElement | null>;
}

// This is private context
// for ModalOverlay + ModalContent.
// Do not import and use directly.
export const ModalContext = createContext<ModalContext | null>(null);

export const ModalOverlay = forwardRef(
  (props: Props, forwardedRef: ForwardedRef<HTMLDivElement>) => {
    const {
      children,
      onDismiss,
      isOpen,
      initialFocusRef,
      className,
      ...divProps
    } = props;
    const testid = props['data-testid'];
    const ref = useRef<HTMLDivElement | null>(null);
    const overlayTriggerState = useOverlayTriggerState({
      isOpen,
      onOpenChange(isOpen) {
        if (isOpen === true) {
          return;
        }
        onDismiss?.();
      },
    });

    const { modalProps, underlayProps } = useModalOverlay(
      {
        isDismissable: true,
        // Don't dismiss the modal when the interaction is inside a toast — the
        // toast is portaled to the body so react-aria would otherwise treat a
        // tap on it as an outside interaction.
        shouldCloseOnInteractOutside: (element) =>
          element.closest('[data-toast-region]') === null,
      },
      overlayTriggerState,
      ref
    );

    useLayoutEffect(() => {
      if (
        initialFocusRef?.current === undefined ||
        initialFocusRef?.current === null
      ) {
        return;
      }
      initialFocusRef.current.focus();
    }, []);
    if (isOpen === false) {
      return null;
    }

    return (
      <Overlay>
        <div
          ref={forwardedRef}
          data-testid={testid}
          {...divProps}
          {...underlayProps}
          className={clsx(styles['modal-overlay'], className)}
        >
          <ModalContext.Provider value={{ modalProps, ref }}>
            {children}
          </ModalContext.Provider>
        </div>
      </Overlay>
    );
  }
);
